/* eslint-disable */
import {
	InstancedMesh,
	Matrix4,
	Color,
	LineBasicMaterial,
	LineSegments,
	Vector3,
	Quaternion
} from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { Group, EdgesGeometry } from 'three';
import { getOptimizedCHAINData } from '@/js/three/VOXUtils.js';
import conf from '@/js/conf/conf'
const cdnHost = conf.hosts().cdnHost
const OBJ_MODEL_CACHE = new Map();
const VOXEL_OPACITY = 0.9
class CHAINMesh extends Group {

	constructor( chunk, currentZ = -1, edgeType = 'natural', modelType = 'chain', objModels = null, optimize = 0, highLight = true, keepOrginColor = false ) {

		super();

		const data = chunk.data;
		const size = chunk.size;
		const palette = chunk.palette;
		const fixSide = 67.9999;
		const fixPosY = 0.265;
		const CHAINData = getOptimizedCHAINData(chunk, parseInt(optimize));

		// 颗粒统计信息
		this.partStats = {
			total: 0,
			parts: []
		};

		// 获取渲染颜色（与渲染代码保持一致）
		const getRenderColor = (colorIndex) => {
			if (colorIndex === undefined || colorIndex === null || colorIndex < 0) {
				return '#ffffff';
			}
			if (!palette || !Array.isArray(palette) || colorIndex >= palette.length) {
				return '#ffffff';
			}
			const hex = palette[colorIndex];
			if (hex === undefined || hex === null) {
				return '#ffffff';
			}
			const r = (hex >> 0 & 0xff) / 0xff;
			const g = (hex >> 8 & 0xff) / 0xff;
			const b = (hex >> 16 & 0xff) / 0xff;
			const rHex = Math.round(r * 255).toString(16).padStart(2, '0');
			const gHex = Math.round(g * 255).toString(16).padStart(2, '0');
			const bHex = Math.round(b * 255).toString(16).padStart(2, '0');
			return `#${rHex}${gHex}${bHex}`;
		};

		const getCHAINData = (x, y, z) => {
			if (CHAINData && CHAINData.data) {
				const key = `${x},${y},${z}`;
				const voxelData = CHAINData.data[key];
				if (voxelData) {
					const [c, blkId, show, direction] = voxelData;
					if (!show) {
						return null;
					}
					return { blkId, show, direction };
				}
			}
			return { blkId: '4', show: 1, direction: '' };
		};

		const processObj = () => {
			// 颗粒统计
			const partCounts = {};
			const partColorCounts = {};
			const voxels = [];
			const showFullModel = currentZ === -1 || currentZ > size.z - 1;
			for (let j = 0; j < data.length; j += 4) {
				const x = data[j + 0];
				const y = data[j + 1];
				const z = data[j + 2];
				if (!showFullModel && z > currentZ) continue;
				const chainData = getCHAINData(x, y, z);
				if (chainData === null) continue;
				const { blkId, show, direction } = chainData;
				if (!show || show === 0) continue; // 升级：严格过滤 show=0
				const colorIndex = data[j + 3];
				if (!partCounts[blkId]) {
					partCounts[blkId] = 0;
					partColorCounts[blkId] = {};
				}
				partCounts[blkId]++;
				const color = getRenderColor(colorIndex);
				if (!partColorCounts[blkId][color]) {
					partColorCounts[blkId][color] = 0;
				}
				partColorCounts[blkId][color]++;
				voxels.push({ x, y, z, c: colorIndex, blkId, direction });
			}
			// 更新颗粒统计信息
			this.partStats.total = voxels.length;
			this.partStats.parts = [];
			for (const [blkId, count] of Object.entries(partCounts)) {
				const colors = partColorCounts[blkId];
				this.partStats.parts.push({
					type: blkId,
					count: count,
					color: colors
				});
			}

			// 1. 按 blkId 分类体素
			const voxelGroups = {};
			for (const voxel of voxels) {
				const blkId = voxel.blkId;
				if (!voxelGroups[blkId]) voxelGroups[blkId] = [];
				voxelGroups[blkId].push(voxel);
			}

			// 2. 遍历每种 blkId，分别创建 InstancedMesh 和边线
			Object.keys(voxelGroups).forEach(blkId => {
				const voxels = voxelGroups[blkId];
				const obj = objModels[blkId];
				if (!obj) return; // 没有对应模型则跳过
				let baseMesh = null;
				obj.traverse(child => {
					if (child.isMesh && !baseMesh) baseMesh = child;
				});
				if (!baseMesh) return;

				baseMesh.geometry.computeBoundingBox();
				const bbox = baseMesh.geometry.boundingBox;
				const minSide = Math.min(
					bbox.max.x - bbox.min.x,
					bbox.max.y - bbox.min.y,
					bbox.max.z - bbox.min.z
				);
				let scale = 0.999 / (fixSide || minSide);

				const geometry = baseMesh.geometry;
				const buildInstanced = (list, faded) => {
					if (!list.length) return null;
					const mat = baseMesh.material.clone();
					mat.vertexColors = false;
					if (faded) { mat.transparent = true; mat.opacity = VOXEL_OPACITY; }
					const im = new InstancedMesh(geometry, mat, list.length);
					const color = new Color();
					const matrix = new Matrix4();
					for (let i = 0; i < list.length; i++) {
						const voxel = list[i];
						const px = voxel.x - size.x / 2 + 0.5;
						let py = voxel.z - size.z / 2 + 0.5;
						let pz = -voxel.y + size.y / 2 + 0.5;
						py -= fixPosY * voxel.z;
						let position = new Vector3(px, py, pz);
						const scaleVec = new Vector3(scale, scale, scale);
						let quaternion = new Quaternion();
						if ((voxel.blkId === '4X' || voxel.blkId === '4X4' || voxel.blkId === '4X3') && voxel.direction === 'Y') {
							let offsetPos = 1.5 ;
							if (voxel.blkId === '4X4') {
								offsetPos = offsetPos + 2;
							} else if (voxel.blkId === '4X3') {
								offsetPos = offsetPos + 1;
							}
							quaternion.setFromAxisAngle(new Vector3(0, 1, 0), 3 * Math.PI / 2);
							pz = -voxel.y + size.y / 2 - offsetPos;
							position = new Vector3(px, py, pz);
						}
						matrix.compose(position, quaternion, scaleVec);
						im.setMatrixAt(i, matrix);
						if (faded) {
							color.setHex(0xB3D7D1);
						} else {
							const hex = palette[voxel.c];
							color.setRGB(
								(hex >> 0 & 0xff) / 0xff,
								(hex >> 8 & 0xff) / 0xff,
								(hex >> 16 & 0xff) / 0xff
							);
						}
						im.setColorAt(i, color);
					}
					im.instanceMatrix && (im.instanceMatrix.needsUpdate = true);
					im.instanceColor && (im.instanceColor.needsUpdate = true);
					this.add(im);
					return im;
				};
				const normal = [];
				const faded = [];
				for (let i = 0; i < voxels.length; i++) {
					const voxel = voxels[i];
					const isCurrentLayer = voxel.z === currentZ;
					if (showFullModel || isCurrentLayer || size.z <= currentZ || keepOrginColor) normal.push(voxel);
					else faded.push(voxel);
				}
				buildInstanced(normal, false);
				buildInstanced(faded, true);

				// 边线处理
				const isViewingBlueprint = currentZ >= 0 && currentZ < size.z - 1;
				const shouldShowEdges = edgeType !== 'none' || isViewingBlueprint;
				if (shouldShowEdges) {
					const edgeGeo = new EdgesGeometry(baseMesh.geometry, 15);
					const getVoxelColor = (voxel) => {
						const isCurrentLayer = voxel.z === currentZ;
						if (showFullModel || isCurrentLayer || size.z <= currentZ || keepOrginColor) {
							const hex = palette[voxel.c];
							return new Color(
								(hex >> 0 & 0xff) / 0xff,
								(hex >> 8 & 0xff) / 0xff,
								(hex >> 16 & 0xff) / 0xff
							);
						} else {
							return new Color(0xB3D7D1);
						}
					};
					const getEdgeColor = (voxel) => {
						const isCurrentLayer = voxel.z === currentZ;
						if (isCurrentLayer && highLight) {
							return new Color(0x00ff00);
						} else {
							switch (edgeType) {
								case 'dark':
									return new Color(0x333333);
								case 'natural': {
									const voxelColor = getVoxelColor(voxel);
									const darkerColor = new Color();
									darkerColor.copy(voxelColor);
									darkerColor.multiplyScalar(0.85);
									return darkerColor;
								}
								case 'none':
									return null;
								default:
									return new Color(0x333333);
							}
						}
					};
					for (let i = 0; i < voxels.length; i++) {
						const voxel = voxels[i];
						const px = voxel.x - size.x / 2 + 0.5;
						let py = voxel.z - size.z / 2 + 0.5;
						let pz = -voxel.y + size.y / 2 + 0.5;
						
						// 该层所有体素向下偏移fixPosY*n
						py -= fixPosY * voxel.z;
						
						const edgeColor = getEdgeColor(voxel);
						if (edgeColor === null) continue;
					const isCurrentLayerEdge = voxel.z === currentZ;
					const fadedEdge = !(showFullModel || isCurrentLayerEdge || size.z <= currentZ || keepOrginColor);
					const edgeMaterial = new LineBasicMaterial({ 
						color: edgeColor, 
						depthWrite: false, 
						depthTest: true,
						transparent: fadedEdge,
						opacity: fadedEdge ? VOXEL_OPACITY : 1.0
					});
						const edge = new LineSegments(edgeGeo, edgeMaterial);
						edge.position.set(px, py, pz);
						edge.scale.set(scale + 0.00005, scale + 0.00005, scale + 0.00005);
						if ((voxel.blkId === '4X' || voxel.blkId === '4X4' || voxel.blkId === '4X3') && voxel.direction === 'Y') {
							let offsetPos = 1.5 ;
							if (voxel.blkId === '4X4') {
								offsetPos = offsetPos + 2;
							} else if (voxel.blkId === '4X3') {
								offsetPos = offsetPos + 1;
							}
							edge.rotation.y = 3 * Math.PI / 2;
							pz = -voxel.y + size.y / 2 - offsetPos;
							
							edge.position.set(px, py, pz);
						}
						edge.renderOrder = 1;
						this.add(edge);
					}
				}
			});
		};

		// 加载积木颗粒 
		function loadObjModels(type = 'chain', cb) {
			let names = ['4', '4X'];
			const loader = new OBJLoader();
			let loadCount = 0;
			objModels = {};
			names.forEach(name => {
				const key = `${type}:${name}`;
				let promise = OBJ_MODEL_CACHE.get(key);
				if (!promise) {
					const modelPath = `${cdnHost}obj/${type}/${name}.obj`;
					promise = new Promise((resolve, reject) => {
						loader.load(modelPath, resolve, undefined, reject);
					});
					OBJ_MODEL_CACHE.set(key, promise);
				}
				promise.then((obj) => {
					objModels[name] = obj;
					loadCount++;
					if (loadCount >= names.length) {
						cb(objModels);
					}
				});
			});
		}

		if (objModels) {
			processObj();
		} else {
			loadObjModels(modelType, processObj)
		}

		// 获取颗粒统计信息的方法
		this.getPartStats = () => {
			return this.partStats;
		};

		// 获取全部的颗粒统计信息
		this.getTotalPartStats = () => {
			if (!CHAINData || !CHAINData.data) {
				return { total: 0, parts: [] };
			}
			const totalStats = {
				total: 0,
				parts: []
			};
			const partCounts = {};
			const partColorCounts = {};
			// 遍历 CHAINData.data
			for (const [key, voxelData] of Object.entries(CHAINData.data)) {
				const [c, blkId, show, direction] = voxelData;
				if (!show || !blkId) continue;
				// 统计类型
				if (!partCounts[blkId]) {
					partCounts[blkId] = 0;
					partColorCounts[blkId] = {};
				}
				partCounts[blkId]++;
				// 统计颜色
				const color = getRenderColor(c);
				if (!partColorCounts[blkId][color]) {
					partColorCounts[blkId][color] = 0;
				}
				partColorCounts[blkId][color]++;
				totalStats.total++;
			}
			for (const [blkId, count] of Object.entries(partCounts)) {
				const colors = partColorCounts[blkId];
				totalStats.parts.push({
					type: blkId,
					count: count,
					color: colors
				});
			}
			return totalStats;
		};
		// 获取指定层的颗粒统计信息
		this.getLayerPartStats = (layerZ) => {
			if (!CHAINData || !CHAINData.data) {
				return { total: 0, parts: [] };
			}
			const layerStats = {
				total: 0,
				parts: []
			};
			const layerPartCounts = {};
			const layerPartColorCounts = {};
			for (let j = 0; j < data.length; j += 4) {
				const x = data[j + 0];
				const y = data[j + 1];
				const z = data[j + 2];
				const colorIndex = data[j + 3];
				if (z !== layerZ) continue;
				const key = `${x},${y},${z}`;
				const voxelData = CHAINData.data[key];
				let blkId = '4';
				let show = true;
				if (voxelData) {
					const [c, voxelBlkId, voxelShow, direction] = voxelData;
					blkId = voxelBlkId || '4';
					show = voxelShow !== false && voxelShow !== 0; // 升级：严格过滤 show=0
				}
				if (show === false || show === 0) continue; // 升级：严格过滤 show=0
				if (!layerPartCounts[blkId]) {
					layerPartCounts[blkId] = 0;
					layerPartColorCounts[blkId] = {};
				}
				layerPartCounts[blkId]++;
				const color = getRenderColor(colorIndex);
				if (!layerPartColorCounts[blkId][color]) {
					layerPartColorCounts[blkId][color] = 0;
				}
				layerPartColorCounts[blkId][color]++;
				layerStats.total++;
			}
			for (const [blkId, count] of Object.entries(layerPartCounts)) {
				const colors = layerPartColorCounts[blkId];
				layerStats.parts.push({
					type: blkId,
					count: count,
					color: colors
				});
			}
			return layerStats;
		};
	}

}

export { CHAINMesh };
